/**
 * Who may operate the fleet, and by what authority.
 *
 * The distinction that matters is `source`: a `platform_admins` row is the
 * intended, revocable state; `FLEET_BOOTSTRAP_ADMIN_EMAILS` is a chicken-and-egg
 * workaround that grants owner from an environment variable and is meant to be
 * emptied once the table has a real row. An env-var owner that nobody
 * remembers is a permanent unaudited key to 26 clinics, so it is labelled
 * everywhere it appears rather than merged into one list.
 *
 * The page is now writable, and two rules govern the controls:
 *
 *   - Every write is **owner**, not operator. Authority over who may operate is
 *     the most sensitive thing here; an operator can apply a plan but must not
 *     be able to grant themselves the right to.
 *
 *   - The server refuses to remove or demote the **last active owner**, and the
 *     UI says so before you try rather than only afterwards. Nothing in this
 *     tool can restore an owner: recovery means hand-editing the database or
 *     setting the bootstrap variable and restarting.
 *
 * Deactivating is offered ahead of deleting. They are not the same: deleting
 * discards when the operator was added and when they were last seen, and that
 * history is the only audit trail this table has.
 *
 * ── Compaction pass (daily operator, expert) ──────────────────────────────
 *
 * * The header subtitle is gone; the owner-only rule is enforced by the server
 *   and stated where a write actually happens, not as a standing paragraph.
 * * The "authority comes from the database" callout is gone with it. That is
 *   the intended state, and the intended state is quiet — the Database badge
 *   beside the title says it in two words. The bootstrap and unconfigured
 *   callouts stay in full: those are the ones that need reading.
 * * The table starts in the first viewport, and the editor's four fields are a
 *   two-column grid instead of a stack, so the last-owner warning and the
 *   Save/Remove pair are visible without scrolling the panel.
 * * Every row has an Edit button, so the editor is reachable by keyboard and
 *   not only by clicking a row.
 * * Untouched by design: the last-active-owner guard and its wording, the
 *   deactivate-before-delete preference, "Not recorded" rather than "never
 *   signed in", and the bootstrap owners keeping a card of their own.
 */

import { useMemo, useState } from 'react';

import { Badge, Tag } from '../components/Badge';
import { Callout } from '../components/Callout';
import { PageHeader } from '../components/PageHeader';
import { StatGrid, StatTile } from '../components/StatTile';
import { EmptyBlock, ErrorBlock, SkeletonRows } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { DetailPanel } from '../components/ui/DetailPanel';
import { FilterBar } from '../components/ui/FilterBar';
import { Checkbox, Select, SelectItem, TextInput } from '../components/ui/Form';
import { CopyButton } from '../components/ui/CopyButton';
import { AddIcon, ChevronRightIcon } from '../components/ui/Icon';
import { apiDelete, apiPost, apiPut, describeError } from '../lib/api';
import { formatDateTime, formatNumber, formatRelative, pluralise, UNKNOWN } from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  AdminListResponse,
  AdminResponse,
  RoleName,
  SetupLinkResponse,
} from '../types/api';

import '../styles/page-operators.css';

/**
 * Only the sources that need a sentence. `database` is the intended state and
 * gets none: the badge beside the page title already says where authority comes
 * from, and a callout repeating it in the normal case is a paragraph the
 * operator reads past every day.
 */
const SOURCE_COPY: Record<
  string,
  { title: string; body: string; tone: 'warning' | 'critical' }
> = {
  bootstrap: {
    title: 'Authority comes from the bootstrap environment variable',
    body: 'No platform_admins rows are in effect, so FLEET_BOOTSTRAP_ADMIN_EMAILS is granting owner access. Add real rows and clear that setting — an env-var owner cannot be revoked without a restart and is invisible to any audit of the table.',
    tone: 'warning',
  },
  none: {
    title: 'No source of authority is configured',
    body: 'Neither platform_admins rows nor bootstrap emails are in effect. If you are reading this page, you reached it some other way — most likely the controller is running with dev_no_auth on a loopback bind.',
    tone: 'critical',
  },
};

const ROLE_TONE: Record<string, 'info' | 'neutral'> = {
  owner: 'info',
  operator: 'info',
  viewer: 'neutral',
};

export function AdminsPage() {
  const admins = useResource<AdminListResponse>('/api/admins');
  const rows = admins.data?.admins ?? [];
  const bootstrapEmails = admins.data?.bootstrap_emails ?? [];
  const source = admins.data?.source ?? '';
  const copy = SOURCE_COPY[source];

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdminResponse | null>(null);
  const [adding, setAdding] = useState(false);

  const activeOwners = rows.filter((a) => a.role === 'owner' && a.active).length;

  // Each bootstrap owner is cross-checked against the database rows so the last
  // column can say whether clearing the env var would remove their access.
  const bootstrapRows = useMemo(
    () =>
      bootstrapEmails.map((email) => ({
        email,
        inDatabase: rows.some((a) => a.email.toLowerCase() === email.toLowerCase()),
      })),
    [bootstrapEmails, rows],
  );

  const columns: ReadonlyArray<Column<AdminResponse, string>> = useMemo(
    () => [
      {
        key: 'email',
        header: 'Operator',
        render: (a) => (
          <div className="cell-stack">
            <span className="cell-stack__primary">{a.display_name || a.email}</span>
            {a.display_name ? (
              <span className="cell-stack__secondary">{a.email}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'role',
        header: 'Role',
        render: (a) => <Badge tone={ROLE_TONE[a.role] ?? 'neutral'} small>{a.role}</Badge>,
      },
      {
        key: 'active',
        header: 'Status',
        value: (a) => (a.active ? 1 : 0),
        render: (a) =>
          a.active ? (
            <Badge tone="good" small>Active</Badge>
          ) : (
            <Badge tone="neutral" small>Disabled</Badge>
          ),
      },
      {
        key: 'created_at',
        header: 'Added',
        render: (a) => (
          <span className="muted" title={formatDateTime(a.created_at)}>
            {a.created_at ? formatRelative(a.created_at) : UNKNOWN}
          </span>
        ),
      },
      {
        key: 'last_login_at',
        header: 'Last seen',
        render: (a) =>
          a.last_login_at ? (
            <span title={formatDateTime(a.last_login_at)}>
              {formatRelative(a.last_login_at)}
            </span>
          ) : (
            // Not "never signed in": this column only started being written
            // recently, so an absent value means unrecorded, not inactive.
            <span className="muted" title="No authenticated request recorded since this was first tracked.">
              Not recorded
            </span>
          ),
      },
      {
        key: 'edit',
        header: <span className="visually-hidden">Edit</span>,
        sortable: false,
        width: '2.5rem',
        // The row click is a convenience. This is the keyboard path into the
        // editor, which is the only write this page has.
        render: (a) => (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={ChevronRightIcon}
            iconDescription={`Edit ${a.display_name || a.email}`}
            onClick={() => {
              setAdding(false);
              setEditing(a);
            }}
          />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Operators"
        status={
          admins.data ? (
            <Badge tone={source === 'database' ? 'good' : source === 'bootstrap' ? 'warning' : 'critical'}>
              {source === 'database' ? 'Database' : source === 'bootstrap' ? 'Bootstrap' : 'Unconfigured'}
            </Badge>
          ) : null
        }
        actions={
          <Button size="sm" renderIcon={AddIcon} onClick={() => setAdding(true)}>
            Add operator
          </Button>
        }
      />

      {copy ? (
        <Callout tone={copy.tone} title={copy.title}>
          <p>{copy.body}</p>
        </Callout>
      ) : null}

      {admins.data ? (
        <StatGrid>
          <StatTile label="Operators" value={formatNumber(rows.length)} hint="rows in platform_admins" />
          <StatTile
            label="Owners"
            value={formatNumber(activeOwners)}
            tone={activeOwners === 1 ? 'warning' : 'neutral'}
            hint={
              activeOwners === 1
                ? 'only one — it cannot be removed'
                : `${formatNumber(activeOwners)} can grant access`
            }
          />
          <StatTile
            label="Disabled"
            value={formatNumber(rows.filter((a) => !a.active).length)}
            hint="kept for their history"
          />
          <StatTile
            label="Bootstrap owners"
            value={formatNumber(bootstrapEmails.length)}
            tone={bootstrapEmails.length > 0 ? 'warning' : 'neutral'}
            hint={bootstrapEmails.length > 0 ? 'granted by env var' : 'none — the intended state'}
          />
        </StatGrid>
      ) : null}

      {admins.loading && !admins.data ? (
        <SkeletonRows rows={4} />
      ) : admins.error && !admins.data ? (
        <ErrorBlock what="the operator list" error={admins.error} onRetry={admins.reload} />
      ) : (
        <>
          <FilterBar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Search name, email, role…"
            searchLabel="Search operators"
            count={`${formatNumber(rows.length)} ${pluralise(rows.length, 'operator')}`}
          />
          <Card flush>
            <DataTable<AdminResponse, string>
              rows={rows}
              columns={columns}
              getRowId={(a) => a.email}
              label="Operators"
              initialSortKey="email"
              search={search}
              onRowClick={(a) => {
                setAdding(false);
                setEditing(a);
              }}
              isRowActive={(a) => a.email === editing?.email}
              empty={
                <EmptyBlock
                  title="No operators in the database"
                  action={
                    <Button size="sm" renderIcon={AddIcon} onClick={() => setAdding(true)}>
                      Add the first operator
                    </Button>
                  }
                >
                  Authority is coming from somewhere else — the bootstrap variable, or
                  dev_no_auth. Adding a row here is what makes access revocable.
                </EmptyBlock>
              }
            />
          </Card>
        </>
      )}

      {/* ── bootstrap owners, never merged into the table above ────────── */}
      {bootstrapRows.length > 0 ? (
        <Card tone="warning">
          <CardHeader
            title="Bootstrap owners"
            count={bootstrapRows.length}
            subtitle="FLEET_BOOTSTRAP_ADMIN_EMAILS — owner access that no restart-free change can revoke, and that no audit of the table can see."
          />
          <CardBody>
            <ul className="op-boot">
              {bootstrapRows.map((b) => (
                <li key={b.email} className="op-boot__item">
                  <span className="mono op-boot__email">{b.email}</span>
                  <Tag>owner</Tag>
                  {b.inDatabase ? (
                    <Badge
                      tone="good"
                      small
                      title="This email also has a platform_admins row, so clearing the variable leaves their access in place."
                    >
                      Also in the database
                    </Badge>
                  ) : (
                    <Badge
                      tone="warning"
                      small
                      title="This email has no platform_admins row. Clearing the variable removes their access entirely."
                    >
                      Not in the database — clearing removes access
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      <DetailPanel
        open={adding || editing !== null}
        title={adding ? 'Add operator' : (editing?.display_name || editing?.email) ?? ''}
        subtitle={adding ? 'Grants access to this controller' : editing?.email}
        width="38rem"
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
      >
        <OperatorForm
          // The form seeds its state from `existing` once. Clicking a second
          // row while the panel is open must therefore remount it, or the
          // fields would still hold the first operator's values and save them
          // onto the second one's email.
          key={adding ? 'new' : editing?.email ?? 'none'}
          existing={adding ? null : editing}
          activeOwners={activeOwners}
          onDone={() => {
            setAdding(false);
            setEditing(null);
            admins.reload();
          }}
        />
      </DetailPanel>
    </>
  );
}

function OperatorForm({
  existing,
  activeOwners,
  onDone,
}: {
  existing: AdminResponse | null;
  activeOwners: number;
  onDone: () => void;
}) {
  const [email, setEmail] = useState(existing?.email ?? '');
  const [displayName, setDisplayName] = useState(existing?.display_name ?? '');
  const [role, setRole] = useState<RoleName>(existing?.role ?? 'viewer');
  const [isActive, setIsActive] = useState(existing?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirror of the server's guard, so the reason appears before the attempt
  // rather than only as a 409 afterwards.
  const isLastOwner =
    existing !== null && existing.role === 'owner' && existing.active && activeOwners <= 1;
  const wouldStrandFleet = isLastOwner && (role !== 'owner' || !isActive);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = { role, display_name: displayName.trim() || null, is_active: isActive };
      if (existing) {
        await apiPut(`/api/admins/${encodeURIComponent(existing.email)}`, body);
      } else {
        await apiPost('/api/admins', { email: email.trim(), ...body });
      }
      onDone();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!existing) return;
    setBusy(true);
    setError(null);
    try {
      await apiDelete(`/api/admins/${encodeURIComponent(existing.email)}`);
      onDone();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error ? (
        <Callout tone="critical" title="Could not save">
          {error}
        </Callout>
      ) : null}

      {isLastOwner ? (
        <Callout tone="warning" title="This is the last active owner">
          <p>
            Demoting, disabling or removing them would leave the fleet with no one who can
            grant the role back. Nothing in this tool can restore an owner — recovery
            means editing the database by hand, or setting{' '}
            <code>FLEET_BOOTSTRAP_ADMIN_EMAILS</code> and restarting the controller.
            Promote another owner first.
          </p>
        </Callout>
      ) : null}

      {/* Four fields, two columns. The helper text that survived is the text
          that prevents a mistake: which claim the email is matched against, and
          that disabling is not deleting. */}
      <section className="op-grid">
        <TextInput
          id="op-email"
          labelText="Email"
          helperText="Matched case-insensitively at sign-in. This is the address they log in with."
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={existing !== null}
          placeholder="ops@health-360.co"
        />
        <TextInput
          id="op-name"
          labelText="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Optional"
        />
        <Select
          id="op-role"
          labelText="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as RoleName)}
        >
          <SelectItem value="viewer" text="viewer — read only" />
          <SelectItem value="operator" text="operator — plan, apply, manage nodes" />
          <SelectItem value="owner" text="owner — everything, including access" />
        </Select>
        <Checkbox
          id="op-active"
          labelText="Active"
          checked={isActive}
          helperText="Disabling revokes access but keeps the row, and its history."
          onChange={(_e, { checked }) => setIsActive(checked)}
        />
      </section>

      <section className="stack">
        <div className="row">
          <Button
            onClick={save}
            disabled={busy || wouldStrandFleet || (!existing && !email.trim())}
          >
            {busy ? 'Saving…' : existing ? 'Save changes' : 'Add operator'}
          </Button>
          {existing ? (
            <Button kind="danger" size="md" disabled={busy || isLastOwner} onClick={remove}>
              Remove
            </Button>
          ) : null}
        </div>
        {existing ? (
          <p className="field__help muted">
            Removing discards when they were added and when they were last seen. Disabling
            keeps both — prefer it unless the row was created in error.
          </p>
        ) : null}
      </section>

      {existing ? <OperatorCredentials email={existing.email} /> : (
        <p className="field__help muted">
          Adding someone creates the row and grants the role. It does not let them in
          yet — reopen them here afterwards to issue a setup link or a password.
        </p>
      )}
    </>
  );
}

/**
 * Give an existing operator a way to sign in.
 *
 * Two paths, and the order is the recommendation. A **setup link** leaves the
 * owner without a copy of the credential: the operator sets a secret nobody else
 * ever saw, so there is nothing to leak from a chat log and nothing that stays
 * valid after whoever typed it moves on. **Setting a password** exists because
 * an owner sometimes has to hand over access in a room with no working mail, and
 * the thing people reach for instead is a shared account — which is worse than a
 * known password, because a shared account also destroys the audit trail.
 *
 * Both are owner-only on the server; the controls are shown to everyone and the
 * 403 explains itself, matching how the center credential panels behave.
 */
function OperatorCredentials({ email }: { email: string }) {
  const [busy, setBusy] = useState<'link' | 'password' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [minted, setMinted] = useState<SetupLinkResponse | null>(null);
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);

  // The server returns a relative path on purpose — building an absolute URL
  // there would mean trusting a Host header a request can set. This origin is
  // the one the operator is already looking at.
  const fullLink = minted ? `${window.location.origin}${minted.path}` : '';

  async function createLink() {
    setBusy('link');
    setError(null);
    setDone(false);
    try {
      setMinted(
        await apiPost<SetupLinkResponse>(
          `/api/admins/${encodeURIComponent(email)}/setup-link`,
        ),
      );
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(null);
    }
  }

  async function setDirectPassword() {
    setBusy('password');
    setError(null);
    setMinted(null);
    try {
      await apiPost(`/api/admins/${encodeURIComponent(email)}/password`, { password });
      setPassword('');
      setDone(true);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="stack">
      <h3 className="section__title">Signing in</h3>

      {error ? (
        <Callout tone="critical" title="Could not issue a credential">
          {error}
        </Callout>
      ) : null}

      {minted ? (
        <Callout tone="warning" title="Send this now — it is shown once">
          <p>
            Single-use, and good for {minted.expires_in_hours} hours. Until it is
            redeemed their current password, if they have one, keeps working — so an
            unused link locks nobody out.
          </p>
          <p>
            <code className="secret">{fullLink}</code>
          </p>
          <CopyButton text={fullLink} label="Copy setup link" kind="tertiary" />
        </Callout>
      ) : null}

      {done ? (
        <Callout tone="good" title="Password set">
          Every session they had open has been ended, so the new password is the only
          way back in. Tell them to change it once they are signed in.
        </Callout>
      ) : null}

      <div className="row">
        <Button kind="tertiary" onClick={createLink} disabled={busy !== null}>
          {busy === 'link' ? 'Creating…' : 'Create setup link'}
        </Button>
      </div>
      <p className="field__help muted">
        Preferred: they choose a password you never see.
      </p>

      <TextInput
        id="op-password"
        labelText="Or set a password for them"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        helperText="At least 12 characters. You will know their password, and every session they have open ends immediately."
      />
      <div className="row">
        <Button
          kind="danger"
          size="md"
          onClick={setDirectPassword}
          disabled={busy !== null || password.trim().length < 12}
        >
          {busy === 'password' ? 'Setting…' : 'Set password'}
        </Button>
      </div>
    </section>
  );
}
